import { useState, useMemo, useEffect, useRef } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Switch,
  FormControlLabel,
  Skeleton,
} from '@mui/material'
import {
  INTERVIEW_ROUND_TYPES,
  INTERVIEW_ROUND_FORMATS,
  type InterviewRoundType,
  type InterviewRoundFormat,
} from '../../constants/interview'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import {
  CalendarMonth as CalendarIcon,
  AutoAwesome as AIIcon,
  Visibility as PublishIcon,
  VisibilityOff as DraftIcon,
} from '@mui/icons-material'
import {
  Interview,
  InterviewStatus,
  MockInterviewHistoryEntry,
  interviewsApi,
  publishApi,
} from '../../services/api'
import InterviewRoundCard from './InterviewRoundCard'
import InterviewFollowUpTimeline from './InterviewFollowUpTimeline'
import MockInterviewVoice from './MockInterviewVoice'
import MockInterviewFeedback from './MockInterviewFeedback'
import { useNotification } from '../../hooks/useNotification'
import { useWebSocket } from '../../hooks/useWebSocket'
import { userAtom } from '../../state/auth'
import { clientsAtom } from '../../state/clients'
import {
  interviewStageDefinitionsAtom,
  interviewStagesLoadingAtom,
} from '../../state/interviews'
import { ApiError } from '../../services/api'
import { guideToHtml, type GuideData } from './guideToHtml'
import DialogShell from '../common/DialogShell'
import PanelSection from '../common/PanelSection'
import {
  GUIDE_BEING_PREPARED_COPY,
  GUIDE_BEING_REVIEWED_COPY,
  isGuideHiddenFromClient,
} from '../../utils/clientGuideState'

// The one stage that is not an ordinary status write. Moving an interview here
// runs the interview-offer-accepted endpoint, which also promotes the client
// record to Complete and stamps the placement, so it must not be routed
// through update-interview. The id is fixed on the backend (the lambda writes
// this literal), which is why it can be matched by value here even though the
// rest of the stage set is admin-configured.
const OFFER_ACCEPTED_STAGE_ID = 'offer_accepted'

interface InterviewDetailDialogProps {
  open: boolean
  interview: Interview | null
  onClose: () => void
  onUpdate: (interview: Interview) => void
  // MWA-394 — a host that links to one section (the portal's "guide is ready"
  // toast) names it here and the dialog scrolls there when it opens. Omitted,
  // the dialog opens at the top as before.
  initialSection?: string
}

// Scroll the dialog body so the section marked data-section={key} is at the top.
function scrollBodyToSection(body: HTMLElement | null, key: string) {
  const target = body?.querySelector<HTMLElement>(`[data-section="${key}"]`)
  if (!body || !target) return
  const top =
    target.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  body.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' })
}

// #199 — Per-session coach feedback block. Shows under each mock interview's
// AI feedback card.
// - Staff: authoring UI (textarea + "share with client" toggle + Save).
//   Pre-populates from existing entry.coachFeedbackNotes so coaches can edit.
// - Client: read-only "From your coach" card, only rendered when the coach
//   flagged the notes as shareable (coachFeedbackVisibleToClient). Otherwise
//   the entire block stays hidden — the client never knows internal notes
//   exist for that session.
function CoachFeedbackBlock({
  interview,
  entry,
  isStaff,
  onUpdate,
}: {
  interview: Interview
  entry: MockInterviewHistoryEntry
  isStaff: boolean
  onUpdate: (interview: Interview) => void
}) {
  const { showSuccess, showError } = useNotification()
  const [draftNotes, setDraftNotes] = useState(entry.coachFeedbackNotes || '')
  const [visibleToClient, setVisibleToClient] = useState(
    Boolean(entry.coachFeedbackVisibleToClient)
  )
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  // Re-hydrate local state if the entry changes underneath us (e.g. another
  // coach saved while this dialog was open, or after our own save round-trips
  // back the fresh interview).
  useEffect(() => {
    setDraftNotes(entry.coachFeedbackNotes || '')
    setVisibleToClient(Boolean(entry.coachFeedbackVisibleToClient))
    setEditing(false)
  }, [entry.coachFeedbackNotes, entry.coachFeedbackVisibleToClient, entry.sessionId])

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await interviewsApi.saveMockInterviewCoachFeedback(
        interview.id,
        entry.sessionId,
        draftNotes,
        visibleToClient
      )
      onUpdate(updated)
      setEditing(false)
      showSuccess(draftNotes.trim() ? 'Coach feedback saved' : 'Coach feedback cleared')
    } catch (err: unknown) {
      console.error('Failed to save coach feedback:', err)
      showError(err instanceof Error ? err.message : 'Failed to save coach feedback')
    } finally {
      setSaving(false)
    }
  }

  // Client-side render path: only the notes if the coach shared them.
  if (!isStaff) {
    if (!entry.coachFeedbackNotes || !entry.coachFeedbackVisibleToClient) return null
    return (
      <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'primary.50' }}>
        <Typography variant="caption" color="primary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
          FROM YOUR COACH
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {entry.coachFeedbackNotes}
        </Typography>
      </Paper>
    )
  }

  // Staff render path: authoring UI.
  const hasSavedNotes = Boolean(entry.coachFeedbackNotes)
  const inEditMode = editing || !hasSavedNotes

  return (
    <Paper
      variant="outlined"
      sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderStyle: 'dashed' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
          COACH FEEDBACK {hasSavedNotes && !editing && '(saved)'}
        </Typography>
        {hasSavedNotes && !editing && (
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </Box>

      {!inEditMode && hasSavedNotes ? (
        <>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {entry.coachFeedbackNotes}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, opacity: 0.7 }}>
            <Chip
              size="small"
              label={
                entry.coachFeedbackVisibleToClient ? 'Shared with client' : 'Internal only'
              }
              color={entry.coachFeedbackVisibleToClient ? 'primary' : 'default'}
              variant="outlined"
            />
            {entry.coachFeedbackBy && (
              <Typography variant="caption" color="text.secondary">
                by {entry.coachFeedbackBy}
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <>
          <TextField
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            multiline
            rows={4}
            fullWidth
            size="small"
            placeholder="Your notes for this session — what stood out, what to work on next, follow-ups…"
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={visibleToClient}
                  onChange={(e) => setVisibleToClient(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="caption">
                  {visibleToClient ? 'Share with client' : 'Internal only'}
                </Typography>
              }
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              {editing && (
                <Button
                  size="small"
                  onClick={() => {
                    setDraftNotes(entry.coachFeedbackNotes || '')
                    setVisibleToClient(Boolean(entry.coachFeedbackVisibleToClient))
                    setEditing(false)
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Feedback'}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  )
}

export default function InterviewDetailDialog({
  open,
  interview,
  onClose,
  onUpdate,
  initialSection,
}: InterviewDetailDialogProps) {
  const [user] = useAtom(userAtom)
  const [interviewStages] = useAtom(interviewStageDefinitionsAtom)
  const [stagesLoading] = useAtom(interviewStagesLoadingAtom)
  const [, setClients] = useAtom(clientsAtom)
  const [savingStage, setSavingStage] = useState(false)
  const [generatingGuide, setGeneratingGuide] = useState(false)
  const [guide, setGuide] = useState<GuideData | null>(null)
  const [editedGuide, setEditedGuide] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [addRoundOpen, setAddRoundOpen] = useState(false)
  const [addingRound, setAddingRound] = useState(false)
  const [newRoundType, setNewRoundType] = useState<InterviewRoundType>('phone_screen')
  const [newRoundFormat, setNewRoundFormat] = useState<InterviewRoundFormat>('video_call')
  const [newRoundDate, setNewRoundDate] = useState('')
  const [addRoundError, setAddRoundError] = useState('')
  const { showSuccess, showError } = useNotification()
  const interviewIdRef = useRef<string | null>(null)
  const guideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Synchronous in-flight guard. useState is async — rapid clicks in the same
  // microtask would all see addingRound === false from the stale closure.
  // A ref updates immediately and blocks subsequent invocations before React
  // re-renders the button with disabled=true.
  const addingRoundRef = useRef(false)
  // The dialog body is the only scroll region; the jump strip scrolls it.
  const bodyRef = useRef<HTMLDivElement | null>(null)

  // Clear any pending fallback timer on unmount
  useEffect(() => {
    return () => {
      if (guideTimeoutRef.current) {
        clearTimeout(guideTimeoutRef.current)
        guideTimeoutRef.current = null
      }
    }
  }, [])

  // Check if user is staff (not a client)
  const isStaff = user?.role !== 'client'

  // Keep track of current interview ID for WebSocket handler
  useEffect(() => {
    interviewIdRef.current = interview?.id || null
  }, [interview?.id])

  // WebSocket handlers for guide generation
  const wsHandlers = useMemo(
    () => ({
      interview_guide_generated: (message: { action: string; data: unknown }) => {
        const data = message.data as { interviewId?: string }
        if (data?.interviewId === interviewIdRef.current && interview) {
          if (guideTimeoutRef.current) {
            clearTimeout(guideTimeoutRef.current)
            guideTimeoutRef.current = null
          }
          interviewsApi
            .get(interview.id)
            .then((fresh) => {
              onUpdate(fresh)
              setGuide((fresh.prepGuide as GuideData) ?? null)
              setEditedGuide(fresh.prepGuide ? guideToHtml(fresh.prepGuide as GuideData) : '')
              setGeneratingGuide(false)
              showSuccess('Interview guide generated successfully')
            })
            .catch((err) =>
              console.warn('[InterviewDetailDialog] failed to refresh after guide generated:', err)
            )
        }
      },
      interview_guide_failed: (message: { action: string; data: unknown }) => {
        const data = message.data as { interviewId?: string; error?: string }
        if (data?.interviewId === interviewIdRef.current) {
          if (guideTimeoutRef.current) {
            clearTimeout(guideTimeoutRef.current)
            guideTimeoutRef.current = null
          }
          setGeneratingGuide(false)
          showError(data.error || 'Guide generation did not complete. Try again.')
        }
      },
      mock_interview_feedback_ready: (message: { action: string; data: unknown }) => {
        const data = message.data as { interviewId?: string }
        if (data?.interviewId === interviewIdRef.current && interview) {
          // Re-fetch the interview to pick up the updated mockInterviewHistory
          interviewsApi
            .get(interview.id)
            .then((fresh) => onUpdate(fresh))
            .catch((err) =>
              console.warn('[InterviewDetailDialog] failed to refresh after feedback ready:', err)
            )
        }
      },
      // publish-content sends interview_guide_published to the CLIENT and
      // interview_guide_visibility_changed to the assigned staff member. This
      // listened for the client's message, which staff never receive, so a
      // guide published or hidden elsewhere never refreshed this dialog.
      // Fires on hide as well as publish, which is what a refetch wants.
      interview_guide_visibility_changed: (message: { action: string; data: unknown }) => {
        const data = message.data as { interviewId?: string }
        if (data?.interviewId === interviewIdRef.current && interview) {
          interviewsApi
            .get(interview.id)
            .then((fresh) => onUpdate(fresh))
            .catch((err) =>
              console.warn('[InterviewDetailDialog] failed to refresh after guide visibility changed:', err)
            )
        }
      },
    }),
    [showSuccess, showError, interview, onUpdate]
  )

  // Connect to WebSocket with handlers
  useWebSocket(wsHandlers)

  // Reset state when interview changes
  useEffect(() => {
    if (interview) {
      setGuide(null)
      setEditedGuide('')
      setGeneratingGuide(false)
    }
  }, [interview?.id])

  // When a client opens the dialog on an interview whose prep guide is published
  // but not yet marked viewed, record the view. Idempotent server-side.
  useEffect(() => {
    if (!open || !interview || isStaff) return
    if (
      interview.prepGuide &&
      !interview.prepGuideIsDraft &&
      !interview.prepGuideViewedAt
    ) {
      interviewsApi
        .markPrepGuideViewed(interview.id)
        .then((fresh) => onUpdate(fresh))
        .catch((err) =>
          console.warn('[InterviewDetailDialog] markPrepGuideViewed failed:', err)
        )
    }
  }, [open, interview?.id, interview?.prepGuide, interview?.prepGuideIsDraft, interview?.prepGuideViewedAt, isStaff, onUpdate, interview])

  // Mark unviewed mock feedback entries as viewed by the current role on open.
  useEffect(() => {
    if (!open || !interview) return
    const history = interview.mockInterviewHistory || []
    const viewer: 'client' | 'coach' = isStaff ? 'coach' : 'client'
    const viewedKey = viewer === 'client' ? 'clientViewedFeedbackAt' : 'coachReviewedFeedbackAt'
    const unviewedEntries = history.filter((h) => h && h.feedbackSummary && !h[viewedKey])
    if (unviewedEntries.length === 0) return

    // Fire-and-forget. Each call is idempotent.
    Promise.all(
      unviewedEntries.map((h) =>
        interviewsApi.markMockFeedbackViewed(interview.id, h.sessionId, viewer).catch((err) => {
          console.warn('[InterviewDetailDialog] markMockFeedbackViewed failed:', err)
          return null
        })
      )
    ).then((results) => {
      const last = results.filter(Boolean).pop()
      if (last) onUpdate(last as Interview)
    })
  }, [open, interview?.id, isStaff, onUpdate, interview])

  // Ref for the first unviewed feedback section — we auto-scroll on open.
  const feedbackSectionRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open || !feedbackSectionRef.current) return
    const history = interview?.mockInterviewHistory || []
    const viewedKey = isStaff ? 'coachReviewedFeedbackAt' : 'clientViewedFeedbackAt'
    const hasUnviewed = history.some((h) => h && h.feedbackSummary && !h[viewedKey])
    if (hasUnviewed) {
      // Small delay so the DOM has settled
      const t = setTimeout(() => {
        feedbackSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 150)
      return () => clearTimeout(t)
    }
  }, [open, interview?.id, interview?.mockInterviewHistory, isStaff])

  // MWA-394 — open at the section a link asked for. The body is not in the DOM
  // until the dialog's transition has mounted it, so wait for it to settle.
  // Keyed on the interview id, not the interview, so a refetch of the same
  // interview does not yank the reader back up the page.
  useEffect(() => {
    if (!open || !interview?.id || !initialSection) return
    const t = setTimeout(() => scrollBodyToSection(bodyRef.current, initialSection), 350)
    return () => clearTimeout(t)
  }, [open, interview?.id, initialSection])

  // Quill editor modules configuration
  // MWA-394 — a client reads the guide; update-interview refuses their writes
  // to it, so they get no formatting toolbar and no editable surface.
  const modules = useMemo(
    () => ({
      toolbar: isStaff
        ? [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['clean'],
          ]
        : false,
    }),
    [isStaff]
  )

  // Stage options are admin-configured rows in the interviewStageDefinitions
  // table (Stage Gates), so this list can legitimately be empty — there is no
  // hardcoded fallback set and none should be introduced. Ordered the way the
  // stage table itself is ordered.
  const activeStages = useMemo(
    () =>
      (interviewStages ?? [])
        .filter((stage) => stage.isActive)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)),
    [interviewStages]
  )

  if (!interview) return null

  const displayGuide = guide || interview.prepGuide
  const htmlGuide = displayGuide ? guideToHtml(displayGuide) : ''

  const currentStage = activeStages.find((stage) => stage.stageId === interview.status)
  const stageLabel = (stageId: string) =>
    activeStages.find((stage) => stage.stageId === stageId)?.label || stageId.replace(/_/g, ' ')

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Not scheduled'
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleGenerateGuide = async () => {
    try {
      setGeneratingGuide(true)
      const result = await interviewsApi.generateGuide(interview.clientId, interview.id)

      // Check if the response indicates async processing
      if (result.status === 'generating') {
        // Guide is being generated asynchronously, WebSocket will notify when done
        showSuccess('Generating interview guide... This may take a moment.')

        // Fallback: if the WS event never arrives (dropped connection, missed
        // handler), the 130s timer refetches once and either picks up the
        // completed guide or surfaces a timeout error. Matches the 120s
        // backend timeout + a 10s buffer for DDB write + WS propagation.
        if (guideTimeoutRef.current) clearTimeout(guideTimeoutRef.current)
        guideTimeoutRef.current = setTimeout(async () => {
          guideTimeoutRef.current = null
          try {
            const fresh = await interviewsApi.get(interview.id)
            onUpdate(fresh)
            if (fresh.prepGuide) {
              setGuide(fresh.prepGuide as GuideData)
              setEditedGuide(guideToHtml(fresh.prepGuide as GuideData))
              setGeneratingGuide(false)
              showSuccess('Interview guide ready')
            } else {
              setGeneratingGuide(false)
              showError('Guide generation timed out. Please try again.')
            }
          } catch {
            setGeneratingGuide(false)
            showError('Guide generation timed out. Please try again.')
          }
        }, 130_000)
        return
      }

      // If guide was returned directly (shouldn't happen with async, but handle it)
      if (result.guide) {
        const receivedGuide = result.guide as GuideData
        setGuide(receivedGuide)
        setEditedGuide(guideToHtml(receivedGuide))
        showSuccess('Interview guide generated successfully')
        onUpdate({ ...interview, prepGuide: receivedGuide as Interview['prepGuide'] })
        setGeneratingGuide(false)
      }
    } catch (error) {
      console.error('Error generating interview guide:', error)
      // Only the statuses this lambda itself authors with a sentence written
      // for staff. Passing through every 4xx was too broad: API Gateway answers
      // before the lambda with its own one-word bodies, so a throttled request
      // showed "Too Many Requests" with no advice — and retrying is exactly the
      // right advice there. Everything else keeps the retry wording.
      const REFUSAL_STATUSES = [403, 404, 409]
      const refusal =
        error instanceof ApiError && REFUSAL_STATUSES.includes(error.status)
          ? error.message
          : null
      showError(refusal || 'Guide generation did not complete. Try again.')
      setGeneratingGuide(false)
    }
  }

  const handleSaveGuide = async () => {
    try {
      setSaving(true)
      await interviewsApi.update({ id: interview.id, prepGuide: editedGuide })
      showSuccess('Guide saved successfully')
      onUpdate({ ...interview, prepGuide: editedGuide })
    } catch (error) {
      console.error('Error saving guide:', error)
      showError('Failed to save guide')
    } finally {
      setSaving(false)
    }
  }

  const handleAddRoundSubmit = async () => {
    if (addingRoundRef.current) return
    addingRoundRef.current = true
    setAddingRound(true)
    try {
      const parsedDate = newRoundDate ? new Date(newRoundDate) : null
      if (parsedDate && Number.isNaN(parsedDate.getTime())) {
        setAddRoundError('Enter a valid date and time.')
        return
      }
      const updated = await interviewsApi.update({
        id: interview.id,
        addRound: {
          type: newRoundType,
          format: newRoundFormat,
          // omitted rather than nulled: the API types date as an optional
          // string and stores null for an absent one anyway.
          ...(parsedDate ? { date: parsedDate.toISOString() } : {}),
        },
      })
      onUpdate(updated)
      setAddRoundOpen(false)
      setNewRoundType('phone_screen')
      setNewRoundFormat('video_call')
      setNewRoundDate('')
      setAddRoundError('')
    } catch (err) {
      console.error('Failed to add round:', err)
      // The API rejects a round dated before the previous one and names both
      // rounds in its message, which is more use than a generic failure.
      const message = err instanceof Error && err.message ? err.message : 'Failed to add round'
      setAddRoundError(message)
      showError(message)
    } finally {
      addingRoundRef.current = false
      setAddingRound(false)
    }
  }

  // The only writer of interview.status in the app. It moved here from the
  // retired interview board's drag handler, which is why the offer-accepted
  // branch below exists: that board never wrote the terminal stage as an
  // ordinary status update either.
  const handleStageChange = async (nextStageId: string) => {
    if (!interview || nextStageId === interview.status) return
    setSavingStage(true)
    try {
      if (nextStageId === OFFER_ACCEPTED_STAGE_ID) {
        const { interview: updated, client } = await interviewsApi.markOfferAccepted(interview.id)
        onUpdate(updated)
        // The endpoint promoted the client record to Complete. Fold the client
        // it returns into the shared list so the client pipeline shows the
        // promotion without a reload.
        setClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, ...client } : c)))
        showSuccess('Offer accepted. Moving to Complete.')
      } else {
        const updated = await interviewsApi.update({
          id: interview.id,
          status: nextStageId as InterviewStatus,
        })
        onUpdate(updated)
        showSuccess(`Interview moved to ${stageLabel(nextStageId)}`)
      }
    } catch (err) {
      console.error('Failed to update interview stage:', err)
      showError('Failed to update interview stage')
    } finally {
      setSavingStage(false)
    }
  }

  const handleTogglePublish = async () => {
    if (!interview) return
    setPublishing(true)
    try {
      const result = interview.prepGuideIsDraft
        ? await publishApi.publishInterviewGuide(interview.id)
        : await publishApi.unpublishInterviewGuide(interview.id)
      onUpdate({ ...interview, prepGuideIsDraft: result.prepGuideIsDraft })
      showSuccess(
        result.prepGuideIsDraft
          ? 'Interview guide hidden from client'
          : 'Interview guide published to client'
      )
    } catch (error) {
      console.error('Failed to toggle publish:', error)
      showError('Failed to update guide visibility')
    } finally {
      setPublishing(false)
    }
  }

  // Which sections render for this viewer. The jump strip lists exactly these.
  const mockHistory = interview.mockInterviewHistory || []
  const hasMockFeedback = mockHistory.length > 0
  const showRounds = (interview.rounds && interview.rounds.length > 0) || isStaff
  const showCoachPending = isStaff && interview.coachReviewStatus === 'pending'
  const showCoachReviewed =
    interview.coachReviewStatus === 'reviewed' && !!interview.coachReviewedBy
  const showCoach = showCoachPending || showCoachReviewed
  const showMock = !isStaff || hasMockFeedback

  const jumpLinks: { key: string; label: string }[] = []
  if (showRounds) jumpLinks.push({ key: 'rounds', label: 'Rounds' })
  if (isStaff) jumpLinks.push({ key: 'follow', label: 'Follow-up' })
  if (showCoach) jumpLinks.push({ key: 'coach', label: 'Coach review' })
  if (showMock) {
    jumpLinks.push({ key: 'mock', label: hasMockFeedback ? 'Mock feedback' : 'Mock interview' })
  }
  jumpLinks.push({ key: 'guide', label: 'Prep guide' })

  const handleJump = (key: string) => scrollBodyToSection(bodyRef.current, key)

  // Staff move the interview through its stages from here — this is the only
  // surface that writes the stage. Clients see it read-only. The chip variants
  // sit in a Box so the header's phone-width stretch does not widen the chip.
  const stageControl = !isStaff ? (
    <Box>
      <Chip
        label={stageLabel(interview.status)}
        size="small"
        color="primary"
        variant="outlined"
      />
    </Box>
  ) : stagesLoading && activeStages.length === 0 ? (
    <Skeleton variant="rounded" width={200} height={40} />
  ) : activeStages.length === 0 ? (
    // The stage table is admin-configured and can be empty (or its read
    // can have failed), so there is nothing to choose from. Show the
    // stage the interview actually carries instead of an empty menu.
    <Box>
      <Tooltip title="No interview stages are configured. An admin can add them under Stage Gates.">
        <Chip
          label={stageLabel(interview.status)}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Tooltip>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
        <InputLabel id="interview-stage-label">Stage</InputLabel>
        <Select
          labelId="interview-stage-label"
          id="interview-stage"
          label="Stage"
          value={interview.status as string}
          disabled={savingStage}
          onChange={(e) => handleStageChange(e.target.value)}
        >
          {/* A stage that has since been deactivated or renamed away
              still has to be selectable-as-current, or the control
              would render blank on the interviews sitting in it. */}
          {!currentStage && (
            <MenuItem value={interview.status} disabled>
              {stageLabel(interview.status)}
            </MenuItem>
          )}
          {activeStages.map((stage) => (
            <MenuItem key={stage.stageId} value={stage.stageId}>
              {stage.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {savingStage && <CircularProgress size={20} />}
    </Box>
  )

  const jumpStrip = (
    <Box
      component="nav"
      aria-label="Jump to section"
      sx={{
        display: 'flex',
        // One row that scrolls inside the strip on a phone, so 44px targets do
        // not stack into a tall block above the content.
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        overflowX: { xs: 'auto', sm: 'visible' },
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        gap: 0.75,
        px: { xs: 2, sm: 2.5 },
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        flexShrink: 0,
      }}
    >
      {jumpLinks.map((link) => (
        <Button
          key={link.key}
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => handleJump(link.key)}
          sx={{
            borderRadius: 99,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            minHeight: { xs: 44, sm: 28 },
            flexShrink: 0,
            px: 1.5,
            py: 0,
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.secondary' },
          }}
        >
          {link.label}
        </Button>
      ))}
    </Box>
  )

  const guideAside =
    isStaff && displayGuide ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {interview.prepGuideIsDraft && (
          <Chip
            icon={<DraftIcon sx={{ fontSize: 14 }} />}
            label="Draft"
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        )}
        <Tooltip title={interview.prepGuideIsDraft ? 'Publish to client' : 'Hide from client'}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              publishing ? (
                <CircularProgress size={16} />
              ) : interview.prepGuideIsDraft ? (
                <PublishIcon />
              ) : (
                <DraftIcon />
              )
            }
            onClick={handleTogglePublish}
            disabled={publishing || saving || generatingGuide}
            color={interview.prepGuideIsDraft ? 'success' : 'inherit'}
          >
            {interview.prepGuideIsDraft ? 'Publish' : 'Unpublish'}
          </Button>
        </Tooltip>
      </Box>
    ) : undefined

  // The first section in the working column has no rule above it, so it needs
  // its own bottom space before the next section's rule.
  const isFirstSection = (key: string) => jumpLinks[0]?.key === key
  const sectionSx = (key: string) => (isFirstSection(key) ? { pb: 2 } : undefined)

  // The date and notes are reference, not work, so they sit in the rail.
  const rail = (
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 1.75, m: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" component="dt" sx={{ display: 'block' }}>
          Interview date
        </Typography>
        <Box component="dd" sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <CalendarIcon color="action" fontSize="small" />
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {formatDate(interview.interviewDate)}
          </Typography>
        </Box>
      </Box>

      {interview.notes && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" component="dt" sx={{ display: 'block' }}>
            Notes
          </Typography>
          <Typography variant="body2" component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {interview.notes}
          </Typography>
        </Box>
      )}
    </Box>
  )

  const main = (
    <>
        {/* Interview Rounds */}
        {showRounds && (
          <Box data-section="rounds" sx={sectionSx('rounds')}>
            <PanelSection
              first={isFirstSection('rounds')}
              dense
              title="Interview Rounds"
              aside={
                isStaff ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAddRoundOpen(true)}
                    disabled={addingRound}
                  >
                    + Add Round
                  </Button>
                ) : undefined
              }
            >
              {interview.rounds && interview.rounds.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {interview.rounds.map((round) => (
                    <InterviewRoundCard
                      key={round.roundNumber}
                      round={round}
                      onUpdateRound={async (roundNumber, updates) => {
                        const updated = await interviewsApi.update({ id: interview.id, updateRound: { roundNumber, ...updates } })
                        onUpdate(updated)
                      }}
                      onDeleteRound={async (roundNumber) => {
                        const updated = await interviewsApi.update({ id: interview.id, deleteRound: { roundNumber } })
                        onUpdate(updated)
                      }}
                      readOnly={!isStaff}
                    />
                  ))}
                </Box>
              )}
            </PanelSection>
          </Box>
        )}

        {/* Follow-Up (staff only) — hiring-side thank-you / follow-up
            history, scoped to this interview. Interview half of the two
            follow-up flows; the networking half lives in ContactDetailPanel
            and shares this same interaction plumbing. Gated like its
            neighbours so reusing this dialog in the client portal cannot
            hand a client a write form onto hiring-side history. */}
        {isStaff && (
          <Box data-section="follow" sx={sectionSx('follow')}>
            <PanelSection first={isFirstSection('follow')} dense title="Follow-Up">
              <InterviewFollowUpTimeline interviewId={interview.id} />
            </PanelSection>
          </Box>
        )}

        {/* Coach Review */}
        {showCoach && (
          <Box data-section="coach" sx={sectionSx('coach')}>
            <PanelSection first={isFirstSection('coach')} dense title="Coach review">
              {showCoachPending && (
                <>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Round 1 debrief logged — coach review needed
                  </Alert>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Coach review notes (optional)..."
                      id="coach-review-notes"
                    />
                    <Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={async () => {
                          try {
                            const notesEl = document.getElementById('coach-review-notes') as HTMLTextAreaElement | null
                            const updated = await interviewsApi.update({
                              id: interview.id,
                              coachReviewStatus: 'reviewed',
                              coachReviewNotes: notesEl?.value || '',
                            })
                            onUpdate(updated)
                          } catch (error) {
                            console.error('Failed to mark as reviewed:', error)
                          }
                        }}
                      >
                        Mark as Reviewed
                      </Button>
                    </Box>
                  </Box>
                </>
              )}

              {showCoachReviewed && (
                <Box>
                  <Chip
                    label={`Reviewed by ${interview.coachReviewedBy?.split('@')[0]} on ${interview.coachReviewedAt ? new Date(interview.coachReviewedAt).toLocaleDateString() : ''}`}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: '0.72rem' }}
                  />
                  {interview.coachReviewNotes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {interview.coachReviewNotes}
                    </Typography>
                  )}
                </Box>
              )}
            </PanelSection>
          </Box>
        )}

        {/* Mock Interview — voice + feedback */}
        {showMock && (
          <Box data-section="mock" sx={sectionSx('mock')}>
            <PanelSection
              first={isFirstSection('mock')}
              dense
              title={hasMockFeedback ? 'Mock feedback' : 'Mock interview'}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!isStaff && (
                  <MockInterviewVoice
                    interviewId={interview.id}
                    canStart={
                      !!interview.prepGuide &&
                      interview.prepGuideIsDraft !== true &&
                      !!interview.prepGuideViewedAt &&
                      interview.status !== 'offer_accepted'
                    }
                    lockedReason={
                      !interview.prepGuide
                        ? isGuideHiddenFromClient(interview)
                          ? GUIDE_BEING_REVIEWED_COPY
                          : GUIDE_BEING_PREPARED_COPY
                        : interview.prepGuideIsDraft
                          ? GUIDE_BEING_REVIEWED_COPY
                          : !interview.prepGuideViewedAt
                            ? 'Review your prep guide first, then you can start the mock interview.'
                            : interview.status === 'offer_accepted'
                              ? 'Mock interviews are disabled once an offer has been accepted.'
                              : undefined
                    }
                    onSessionEnded={() => {
                      // Refetch to pick up the new history entry
                      interviewsApi
                        .get(interview.id)
                        .then((fresh) => onUpdate(fresh))
                        .catch((err) =>
                          console.warn('[InterviewDetailDialog] refetch after session end failed:', err)
                        )
                    }}
                  />
                )}

                {/* Mock interview feedback list — most recent first */}
                {hasMockFeedback && (
                  <Box ref={feedbackSectionRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {mockHistory
                      .slice()
                      .reverse()
                      .map((entry) => (
                        <Box key={entry.sessionId}>
                          <MockInterviewFeedback
                            entry={entry}
                            viewer={isStaff ? 'coach' : 'client'}
                            onRetry={() =>
                              interviewsApi
                                .retryMockInterviewFeedback(interview.id, entry.sessionId)
                                .then(() => interviewsApi.get(interview.id))
                                .then((fresh) => onUpdate(fresh))
                                .catch((err) => showError(err?.message || 'Retry failed'))
                            }
                          />
                          {/* #199 — Coach feedback block: authoring UI for staff,
                              read-only "From your coach" card for clients when the
                              coach has flagged the notes as shareable. */}
                          <CoachFeedbackBlock
                            interview={interview}
                            entry={entry}
                            isStaff={isStaff}
                            onUpdate={onUpdate}
                          />
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
            </PanelSection>
          </Box>
        )}

        <Box data-section="guide" sx={sectionSx('guide')}>
          <PanelSection first={isFirstSection('guide')} dense title="AI Interview Guide" aside={guideAside}>
            {generatingGuide && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">
                    Generating your personalized interview guide... This may take 30-60 seconds.
                  </Typography>
                </Box>
              </Alert>
            )}

            {(editedGuide || htmlGuide) ? (
              <Box
                sx={{
                  '& .ql-container': {
                    minHeight: 300,
                    maxHeight: 400,
                    overflow: 'auto',
                    fontSize: '14px',
                  },
                  '& .ql-editor': {
                    minHeight: 300,
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={editedGuide || htmlGuide}
                  onChange={setEditedGuide}
                  readOnly={!isStaff}
                  modules={modules}
                  placeholder="Interview guide will appear here..."
                />
              </Box>
            ) : (
              !generatingGuide && (
                <Typography variant="body2" color="text.secondary">
                  {!isStaff && isGuideHiddenFromClient(interview)
                    ? GUIDE_BEING_REVIEWED_COPY
                    : 'No guide yet.'}
                </Typography>
              )
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              {isStaff && (
                <Button
                  variant="outlined"
                  startIcon={generatingGuide ? <CircularProgress size={16} /> : <AIIcon />}
                  onClick={handleGenerateGuide}
                  disabled={generatingGuide || saving}
                >
                  {generatingGuide ? 'Generating...' : displayGuide ? 'Regenerate Guide' : 'Generate Guide'}
                </Button>
              )}
              {isStaff && (editedGuide || htmlGuide) && (
                <Button
                  variant="contained"
                  onClick={handleSaveGuide}
                  disabled={saving || generatingGuide}
                >
                  {saving ? 'Saving...' : 'Save Guide'}
                </Button>
              )}
            </Box>
          </PanelSection>
        </Box>
    </>
  )

  return (
    <>
      <DialogShell
        open={open}
        onClose={onClose}
        maxWidth="md"
        title={interview.clientName}
        subtitle={`${interview.company} · ${interview.role}`}
        extra={stageControl}
        extraOwnRowOnPhone
        belowHeader={jumpStrip}
        bodyRef={bodyRef}
        split={{ main, rail }}
        footer={<Button onClick={onClose}>Close</Button>}
      >
        {null}
      </DialogShell>

      {/* Add Round sub-dialog */}
      <DialogShell
        open={addRoundOpen}
        onClose={() => !addingRound && setAddRoundOpen(false)}
        maxWidth="xs"
        compact
        title="Add Interview Round"
        footer={
          <>
            <Button
              onClick={() => {
                setAddRoundOpen(false)
                setAddRoundError('')
              }}
              disabled={addingRound}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddRoundSubmit}
              disabled={addingRound}
              startIcon={addingRound ? <CircularProgress size={16} /> : undefined}
            >
              {addingRound ? 'Adding...' : 'Add Round'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="round-type-label">Type</InputLabel>
            <Select
              labelId="round-type-label"
              label="Type"
              value={newRoundType}
              onChange={(e) => setNewRoundType(e.target.value as InterviewRoundType)}
            >
              {INTERVIEW_ROUND_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="round-format-label">Format</InputLabel>
            <Select
              labelId="round-format-label"
              label="Format"
              value={newRoundFormat}
              onChange={(e) => setNewRoundFormat(e.target.value as InterviewRoundFormat)}
            >
              {INTERVIEW_ROUND_FORMATS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* The date is what the thank-you and follow-up reminders reckon
              from, so a round with no date can never report itself overdue. */}
          <TextField
            label="Interview date"
            type="datetime-local"
            size="small"
            fullWidth
            value={newRoundDate}
            onChange={(e) => setNewRoundDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={addingRound}
            helperText="Optional, but the thank-you and follow-up reminders need it."
          />
          {addRoundError && <Alert severity="error">{addRoundError}</Alert>}
        </Box>
      </DialogShell>
    </>
  )
}
